import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Icon from "../../../components/AppIcon";
import FakeAppChrome from "../FakeAppChrome";
import { TOUR_WHATSAPP_SCRIPT } from "../tourWhatsAppScript";

function formatWhatsAppText(text) {
  const parts = String(text || "").split(/(\*[^*]+\*|_[^_]+_)/g);
  return parts.map((part, index) => {
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      return (
        <strong key={index} className="font-semibold">
          {part.slice(1, -1)}
        </strong>
      );
    }
    if (part.startsWith("_") && part.endsWith("_") && part.length > 2) {
      return (
        <em key={index} className="italic opacity-90">
          {part.slice(1, -1)}
        </em>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function TypingDots() {
  return (
    <div className="inline-flex items-center gap-1 px-1 py-0.5">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-zinc-500"
          animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
          transition={{
            duration: 0.9,
            repeat: Infinity,
            delay: i * 0.15,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

function ChatBubble({ message }) {
  const isUser = message.from === "user";

  if (message.kind === "pdf") {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 10, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="max-w-[85%] self-start"
      >
        <div className="overflow-hidden rounded-lg rounded-tl-sm bg-white text-zinc-900 shadow-sm">
          <div className="flex items-center gap-3 border-b border-zinc-100 bg-zinc-50 px-3 py-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-500/15">
              <Icon name="FileText" size={20} className="text-red-600" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{message.text}</p>
              <p className="text-[11px] text-zinc-500">PDF · Documento</p>
            </div>
          </div>
          <div className="px-3 py-2 text-[11px] text-[#027eb5]">
            *Neo Bot:* documento gerado
          </div>
        </div>
      </motion.div>
    );
  }

  if (message.kind === "link") {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 10, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="max-w-[85%] self-start"
      >
        <div className="rounded-lg rounded-tl-sm bg-white px-3 py-2 text-sm text-zinc-900 shadow-sm">
          <p className="whitespace-pre-wrap leading-relaxed">
            <span className="font-semibold text-[#075e54]">Neo Bot: </span>
            {formatWhatsAppText(message.text)}
          </p>
          <div className="mt-2 rounded-md border border-[#25d366]/40 bg-[#25d366]/10 px-3 py-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-[#128c7e]">
              Link de assinatura
            </p>
            <p className="mt-1 text-sm font-semibold text-[#027eb5] underline underline-offset-2">
              {message.linkLabel}
            </p>
            <p className="mt-0.5 truncate text-[10px] text-zinc-500">
              {message.linkUrl}
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      className={
        isUser
          ? "max-w-[85%] self-end"
          : "max-w-[85%] self-start"
      }
    >
      <div
        className={
          isUser
            ? "rounded-lg rounded-tr-sm bg-[#d9fdd3] px-3 py-2 text-sm text-zinc-900 shadow-sm"
            : "rounded-lg rounded-tl-sm bg-white px-3 py-2 text-sm text-zinc-900 shadow-sm"
        }
      >
        {!isUser ? (
          <span className="mb-0.5 block text-[11px] font-semibold text-[#075e54]">
            Neo Bot
          </span>
        ) : null}
        <p className="whitespace-pre-wrap leading-relaxed">
          {formatWhatsAppText(message.text)}
        </p>
      </div>
    </motion.div>
  );
}

const MoreFeaturesScene = () => {
  const [visibleCount, setVisibleCount] = useState(0);
  const [typing, setTyping] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    const timers = [];
    let cancelled = false;

    TOUR_WHATSAPP_SCRIPT.forEach((step, index) => {
      const showTyping =
        step.from === "bot" &&
        index > 0 &&
        TOUR_WHATSAPP_SCRIPT[index - 1]?.from === "user";

      if (showTyping) {
        timers.push(
          window.setTimeout(() => {
            if (!cancelled) setTyping(true);
          }, Math.max(0, step.delayMs - 700))
        );
      }

      timers.push(
        window.setTimeout(() => {
          if (cancelled) return;
          setTyping(false);
          setVisibleCount(index + 1);
        }, step.delayMs)
      );
    });

    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [visibleCount, typing]);

  const visibleMessages = TOUR_WHATSAPP_SCRIPT.slice(0, visibleCount);

  return (
    <FakeAppChrome activeNavId="crm">
      <div className="flex min-h-full flex-col items-center justify-start gap-4 p-3 sm:p-6 lg:flex-row lg:items-stretch lg:justify-center lg:gap-8">
        <div className="w-full max-w-sm shrink-0 lg:max-w-[24rem]">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="overflow-hidden rounded-[1.75rem] border border-zinc-700/40 bg-[#0b141a] shadow-elevation-lg"
          >
            {/* WhatsApp-like chrome */}
            <div className="flex items-center gap-3 bg-[#075e54] px-3 py-3 text-white">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
                <Icon name="Bot" size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">Neo Bot</p>
                <p className="truncate text-[11px] text-white/75">
                  neo-modelos · online
                </p>
              </div>
              <Icon name="MoreVertical" size={18} className="opacity-80" />
            </div>

            <div
              ref={listRef}
              className="flex h-[min(62dvh,32rem)] flex-col gap-2 overflow-y-auto px-3 py-3"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(11,20,26,0.92), rgba(11,20,26,0.92)), radial-gradient(circle at 20% 20%, #1f2c34 0, transparent 40%), radial-gradient(circle at 80% 0%, #1a2a32 0, transparent 35%)",
                backgroundColor: "#0b141a",
              }}
            >
              <AnimatePresence initial={false}>
                {visibleMessages.map((message) => (
                  <ChatBubble key={message.id} message={message} />
                ))}
              </AnimatePresence>

              {typing ? (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="max-w-[40%] self-start rounded-lg rounded-tl-sm bg-white px-3 py-2 shadow-sm"
                >
                  <TypingDots />
                </motion.div>
              ) : null}
            </div>

            <div className="flex items-center gap-2 bg-[#1f2c34] px-3 py-2">
              <div className="flex-1 rounded-full bg-[#2a3942] px-4 py-2 text-xs text-zinc-400">
                Mensagem
              </div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#00a884] text-white">
                <Icon name="Mic" size={16} />
              </div>
            </div>
          </motion.div>
        </div>

        <div className="w-full max-w-md space-y-3 lg:pt-6">
          <p className="text-sm font-semibold text-foreground">
            Mesmo fluxo do Neo Bot no WhatsApp
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <Icon name="ListOrdered" size={16} className="mt-0.5 shrink-0 text-primary" />
              Escolhe um modelo (ex.: autorização de venda)
            </li>
            <li className="flex gap-2">
              <Icon name="Sparkles" size={16} className="mt-0.5 shrink-0 text-primary" />
              Envia os dados em texto livre
            </li>
            <li className="flex gap-2">
              <Icon name="FileText" size={16} className="mt-0.5 shrink-0 text-primary" />
              Recebe o PDF na conversa
            </li>
            <li className="flex gap-2">
              <Icon name="PenLine" size={16} className="mt-0.5 shrink-0 text-primary" />
              Detecta campos e envia o link de assinatura
            </li>
          </ul>
          <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">CRM + WhatsApp</p>
            <p className="mt-1 leading-relaxed">
              Leads e respostas padrão ficam no CRM; o bot usa os modelos da
              conta para gerar e assinar sem sair da conversa.
            </p>
          </div>
        </div>
      </div>
    </FakeAppChrome>
  );
};

export default MoreFeaturesScene;
